import { forwardRef, type ReactNode } from 'react';
import { cn } from '@/lib/utils';

interface CollapsedPanelStripeProps {
  label: string;
  onClick: () => void;
  position: 'left' | 'right';
  className?: string;
}

/**
 * A thin vertical stripe shown when a panel is collapsed.
 * Clicking it expands the panel.
 */
export function CollapsedPanelStripe({
  label,
  onClick,
  position,
  className,
}: CollapsedPanelStripeProps) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'flex h-full w-10 flex-shrink-0 cursor-pointer items-center justify-center',
        'bg-muted/50 transition-colors duration-150 hover:bg-muted',
        'border-border',
        position === 'left' ? 'border-r' : 'border-l',
        className,
      )}
      title={`Expand ${label}`}
      aria-label={`Expand ${label} panel`}
    >
      <span
        className="whitespace-nowrap text-xs font-medium text-muted-foreground"
        style={{
          writingMode: 'vertical-rl',
          textOrientation: 'mixed',
          transform: position === 'left' ? 'rotate(180deg)' : undefined,
        }}
      >
        {label}
      </span>
    </button>
  );
}

interface CollapsiblePanelContentProps {
  children: ReactNode;
  isCollapsed: boolean;
  label: string;
  onExpand: () => void;
  position: 'left' | 'right';
  className?: string;
  showStripe?: boolean;
}

/**
 * Wrapper that shows either the panel content or a collapsed stripe.
 *
 * IMPORTANT: children are always mounted — we use CSS to hide them when
 * collapsed instead of unmounting. This preserves the state of heavy
 * components like the Monaco editor (syntax highlighting, undo history,
 * cursor position) and the R3F Canvas (WebGL context, scene state).
 *
 * The collapsed stripe is shown/hidden with the inverse logic.
 */
export const CollapsiblePanelContent = forwardRef<
  HTMLDivElement,
  CollapsiblePanelContentProps
>(
  (
    {
      children,
      isCollapsed,
      label,
      onExpand,
      position,
      className,
      showStripe = true,
    },
    ref,
  ) => {
    return (
      <div ref={ref} className="relative h-full w-full">
        {/* Collapsed stripe — visible only when collapsed */}
        {isCollapsed && showStripe && (
          <CollapsedPanelStripe
            label={label}
            onClick={onExpand}
            position={position}
          />
        )}

        {/* Panel content — always mounted, hidden via CSS when collapsed */}
        <div
          className={cn(
            'h-full w-full',
            isCollapsed && 'invisible absolute inset-0 overflow-hidden',
            className,
          )}
        >
          {children}
        </div>
      </div>
    );
  },
);

CollapsiblePanelContent.displayName = 'CollapsiblePanelContent';
